/*
  Robles Veterinaria · Carrito lateral (rediseño octubre 2026)
  Basado en cart-drawer.js del tema Minion 4.2.0. Cambios:
  - "Deshacer" al quitar un producto.
  - Recomendados que se piden solo al abrir el carrito (una vez por producto base).
  - Botón "Agregar" de recomendados sin recargar la página.
  - Render tolerante: si alguna sección no llega, no rompe el carrito.
  Sin librerías ni dependencias nuevas.
*/
const RV_RECS_CACHE = new Map();
const RV_UNDO_MS = 5000;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', this.onDrawerClick.bind(this));
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[id*="cart-icon-bubble"]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.loadRecommendations();
    });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn || this, focusElement);
    }, { once: true });

    document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.paddingRight = '';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* Lo usan product-form.js, cart-discount.js y este archivo. */
  renderContents(parsedState, options = {}) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState.id;
    if (!options.keepUndo) this.clearUndo();
    this.renderSections(parsedState.sections);

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  renderSections(sections) {
    if (!sections) return;

    const drawerSource = this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
    const drawerTarget = document.getElementById('CartDrawer');
    if (drawerSource && drawerTarget) drawerTarget.innerHTML = drawerSource.innerHTML;

    const bubbleSource = this.getSectionDOM(sections['cart-icon-bubble']);
    if (bubbleSource) {
      ['cart-icon-bubble', 'cart-icon-bubble--mobile'].forEach((id) => {
        const element = document.getElementById(id);
        if (element) element.innerHTML = bubbleSource.innerHTML;
      });
    }

    const freeDeliverySource = this.getSectionDOM(sections['cart-free-delivery']);
    if (freeDeliverySource) {
      ['product-page-free-delivery', 'cart-free-delivery'].forEach((id) => {
        const element = document.getElementById(id);
        if (element) element.innerHTML = freeDeliverySource.innerHTML;
      });
    }
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
      { id: 'cart-free-delivery' }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  /* ---------- Clics dentro del carrito ---------- */
  onDrawerClick(event) {
    const undoButton = event.target.closest('[data-rv-undo]');
    if (undoButton) {
      event.preventDefault();
      this.undoRemove(undoButton);
      return;
    }
    const addButton = event.target.closest('[data-rv-add]');
    if (addButton) {
      event.preventDefault();
      this.quickAdd(addButton);
    }
  }

  addItems(items, options = {}) {
    const body = JSON.stringify({
      items,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    });

    return fetch(`${routes.cart_add_url}.js`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message || window.cartStrings.error);
        this.classList.remove('is-empty');
        this.renderContents(state, options);
        return state;
      });
  }

  quickAdd(button) {
    const id = Number(button.dataset.rvAdd);
    if (!id || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    this.addItems([{ id, quantity: 1 }]).catch((error) => {
      button.removeAttribute('aria-busy');
      this.showError(error.message);
    });
  }

  showError(message) {
    const errors = this.querySelector('#CartDrawer-CartErrors');
    if (errors) errors.textContent = message || window.cartStrings.error;
  }

  /* ---------- Deshacer ---------- */
  setUndo(data) {
    this.undoData = data;
    clearTimeout(this.undoTimer);
    this.showUndo();
    this.undoTimer = setTimeout(() => this.clearUndo(), RV_UNDO_MS);
  }

  showUndo() {
    const bar = this.querySelector('[data-rv-undo-bar]');
    if (!bar || !this.undoData) return;
    const text = bar.querySelector('[data-rv-undo-text]');
    if (text) text.textContent = `Quitaste ${this.undoData.title}`;
    bar.hidden = false;
  }

  clearUndo() {
    this.undoData = null;
    clearTimeout(this.undoTimer);
    this.querySelectorAll('[data-rv-undo-bar]').forEach((bar) => { bar.hidden = true; });
  }

  undoRemove(button) {
    const data = this.undoData;
    if (!data || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    const item = { id: data.id, quantity: data.quantity };
    if (data.properties) item.properties = data.properties;
    if (data.sellingPlan) item.selling_plan = data.sellingPlan;

    this.addItems([item]).catch((error) => {
      this.clearUndo();
      this.showError(error.message);
    });
  }

  /* ---------- Recomendados (solo al abrir) ---------- */
  loadRecommendations() {
    const box = this.querySelector('[data-rv-recs]');
    if (!box || box.dataset.loaded === 'true' || !box.dataset.url) return;
    box.dataset.loaded = 'true';

    const url = box.dataset.url;
    let request = RV_RECS_CACHE.get(url);
    if (!request) {
      request = fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => '');
      RV_RECS_CACHE.set(url, request);
    }

    request.then((html) => {
      if (!html || !box.isConnected) return;
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rv-recs-list]');
      const track = box.querySelector('[data-rv-recs-track]');
      if (!list || !track) return;

      const inCart = (box.dataset.inCart || '').split(',');
      const max = Number(box.dataset.max) || 3;
      const cards = Array.from(list.querySelectorAll('[data-product-id]'))
        .filter((card) => !inCart.includes(card.dataset.productId))
        .slice(0, max);
      if (!cards.length) return;

      track.replaceChildren(...cards);
      box.hidden = false;
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-icon-bubble--mobile', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'product-page-free-delivery', section: 'cart-free-delivery', selector: '.shopify-section' }
    ];
  }

  enableLoading(line) {
    const items = document.getElementById('CartDrawer-CartItems');
    if (items) items.classList.add('cart__items--disabled');
    this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const items = document.getElementById('CartDrawer-CartItems');
    if (items) items.classList.remove('cart__items--disabled');
  }

  updateQuantity(line, quantity, name) {
    const drawer = document.querySelector('cart-drawer');
    const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
    const newQuantity = parseInt(quantity, 10) || 0;

    let undo = null;
    if (newQuantity === 0 && lineItem) {
      undo = {
        id: Number(lineItem.dataset.variantId),
        quantity: Number(lineItem.dataset.quantity) || 1,
        title: lineItem.dataset.title || ''
      };
      if (lineItem.dataset.properties) {
        try { undo.properties = JSON.parse(lineItem.dataset.properties); } catch (e) { /* sin propiedades */ }
      }
      if (lineItem.dataset.sellingPlan) undo.sellingPlan = Number(lineItem.dataset.sellingPlan);
    }

    this.enableLoading(line);

    const sections = [...new Set(this.getSectionsToRender().map((section) => section.section))];
    const body = JSON.stringify({
      line,
      quantity: newQuantity,
      sections,
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((state) => {
        const error = state.errors || (state.status ? state.description || state.message : null);
        if (error) {
          const input = document.getElementById(`Drawer-quantity-${line}`);
          if (input) input.value = input.getAttribute('value');
          this.updateLiveRegions(line, error);
          this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          this.disableLoading();
          return;
        }

        if (drawer) drawer.classList.toggle('is-empty', state.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const html = state.sections && state.sections[section.section];
          const container = document.getElementById(section.id);
          if (!html || !container) return;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (!source) return;
          const target = section.selector === '.drawer__inner' ? container.querySelector(section.selector) : container;
          if (target) target.innerHTML = source.innerHTML;
        });

        if (drawer) {
          if (undo) drawer.setUndo(undo);
          else drawer.clearUndo();

          const newLine = document.getElementById(`CartDrawer-Item-${line}`);
          const control = !undo && name && newLine ? newLine.querySelector(`[name="${name}"]`) : null;
          if (control) {
            trapFocus(drawer, control);
          } else if (state.item_count === 0) {
            const empty = drawer.querySelector('.drawer__inner-empty');
            trapFocus(empty || drawer, drawer.querySelector('[data-rv-undo]') || undefined);
          } else {
            trapFocus(drawer, drawer.querySelector('[data-rv-undo]') || drawer.querySelector('.drawer__close'));
          }

          drawer.loadRecommendations();
        }

        this.disableLoading();
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
